import React, { useCallback } from 'react';
import { Box, Circle, FlexListItemStatus, Inscription, List, OrgLockIcon } from '@common-fe/common-fe';
import { costFormater } from '@common-fe/common-fe';

import { LARGE_SIZE_LOGO, LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import regexp from '@/common/regexp';
import { Icon } from '@/components/elements/Icon/Icon';
import { TextWithPointer } from '@/components/elements/TextWithPointer';

import { BankingBalanceItem } from '../../types';

interface Props extends BankingBalanceItem {
  orgType: OrganizationTypes;
  onShowChildren: (id: number, name: string, type: OrganizationTypes) => void;
  onViewDetails: (orgId: number) => void;
  hideShowChildren?: boolean;
}

export const formatStatusToSentenceCase = (status: string): string => {
  const formatted = status.replace(regexp.DASH_SYMBOL, ' ').toLowerCase();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const Status = ({ status }: { status: string }) => (
  <Box direction="row" align="center">
    <Inscription margin={{ right: 'spacing8' }}>
      {formatStatusToSentenceCase(status)}
    </Inscription>
    <Circle status={status} />
  </Box>
);

const BankingBalanceListItem: React.FC<Props> = ({
  orgId,
  orgName,
  orgType,
  hsaBalance,
  notionalBalance,
  status,
  isLocked,
  logoUrl,
  onShowChildren,
  onViewDetails,
  hideShowChildren,
}) => {
  const handleOnClick = useCallback(() => {
    onViewDetails(orgId);
  }, [onViewDetails, orgId]);

  return (
    <List.Item testId={`banking-balance-list_item_${orgId}`}>
      <Box direction="row" align="center" fill>
        <Box direction="row" align="center" style={{ flex: 4 }} gap="spacing24">
          <Box margin={{ right: '40px' }}>
            <Icon
              type={orgType}
              text={orgName}
              src={logoUrl}
              size={LetterIconSizeEnum.LOGO}
              height={LARGE_SIZE_LOGO.height}
              width="100px"
            />
          </Box>
          <Box direction="row" align="center">
            {isLocked && (
              <Box data-testid="org-lock-icon" direction="row">
                <OrgLockIcon color="iconSecondary" />
              </Box>
            )}
            <TextWithPointer
              margin={{ left: isLocked ? 'spacing8' : '0' }}
              size="medium"
              weight="bold"
              ellipsisMode
              color="textBody"
              truncate
              onClick={handleOnClick}
            >
              {orgName}
            </TextWithPointer>
          </Box>
        </Box>

        <List.Item.Text
          flex={1.5}
          testId={`banking-balance-list_item_${orgId}_hsa-balance`}
          weight="normal"
        >
          {costFormater(hsaBalance, true)}
        </List.Item.Text>

        <List.Item.Text
          flex={2}
          testId={`banking-balance-list_item_${orgId}_notional-balance`}
          weight="normal"
        >
          {costFormater(notionalBalance, true)}
        </List.Item.Text>

        <List.Item.Text
          flex={1}
          testId={`banking-balance-list_item_${orgId}_status`}
          alignSelf='end'
          weight="normal"
          style={{ whiteSpace: 'nowrap' }}
        >
          <FlexListItemStatus status={formatStatusToSentenceCase(status)} isReverse />
        </List.Item.Text>

        <Box width="24px" align="center" justify="center" margin={{ left: 'spacing24' }}>
          <List.Item.Options>
            <List.Item.Options.Item
              testId={`banking-balance-list_item_${orgId}_view_details`}
              onClick={handleOnClick}
            >
              View details
            </List.Item.Options.Item>
            {!hideShowChildren && (
              <List.Item.Options.Item
                testId={`banking-balance-list_item_${orgId}_show_children`}
                onClick={() => onShowChildren(orgId, orgName, orgType)}
              >
                Show children
              </List.Item.Options.Item>
            )}
          </List.Item.Options>
        </Box>
      </Box>
    </List.Item>
  );
};

export default BankingBalanceListItem;
